/**
 * Desktop state store — Task 6
 * All state shapes extracted from FOM desktop.html source.
 * Each slice mirrors the exact localStorage key and data shape.
 */
import { create } from 'zustand'
import { scheduleOfficerNotifications, notifyPostChange } from '../services/notifications'
import { markDirty, markKeyDirty } from './sync'
import { mergePostingToPersonnel, syncPersonnelToPosting } from './postingBridge'

// ── OOS mirror helper ──────────────────────────────────────────────────
// fom_oos_state is the single persistent source of truth for OOS IDs across
// all equipment categories. The sync layer uses LAST-WRITE-WINS on this key:
// a local edit (add OR remove) is protected from cloud pulls until it has been
// pushed, after which the cloud copy is authoritative. This makes OOS removals
// stick (the old union merge re-added removed items forever).
// Each setter calls this after saving its array so the category stays current.
function mirrorOos(category: string, ids: (string | number)[]): void {
  try {
    const raw = localStorage.getItem('fom_oos_state')
    const state = raw ? JSON.parse(raw) : {}
    state[category] = ids.map(String)
    localStorage.setItem('fom_oos_state', JSON.stringify(state))
    markKeyDirty('fom_oos_state') // protect this edit from pulls until pushed
    markDirty()                   // schedule a push so the change reaches D1
  } catch { /* silent */ }
}

// ── Shift key helper (module scope) ───────────────────────────────────────
// Returns a string like "6/7/2026-Morning" based on ET time.
// Used by both resetShift() and checkShiftBoundary() so the stamps always
// match — if they were computed separately the boundary logic could drift.
export function getShiftKey(): string {
  try {
    const now = new Date()
    const etStr = now.toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false })
    const [hStr, mStr] = etStr.split(':')
    const totalMins = parseInt(hStr) * 60 + parseInt(mStr)
    const shift =
      totalMins >= 6*60+55  && totalMins < 14*60+55 ? 'Morning' :
      totalMins >= 14*60+55 && totalMins < 22*60+55 ? 'Evening' : 'Night'
    const dateStr = now.toLocaleDateString('en-US', { timeZone: 'America/New_York' })
    return `${dateStr}-${shift}`
  } catch {
    const h = new Date().getHours()
    const shift = h >= 7 && h < 15 ? 'Morning' : h >= 15 && h < 23 ? 'Evening' : 'Night'
    return `${new Date().toDateString()}-${shift}`
  }
}

// ── Types ──────────────────────────────────────────────────────────────────

export interface Post {
  post: string
  from: string
  to: string
  isBreak: boolean
  coveredBy?: string
}

export interface PersonnelEntry {
  role: string
  rank?: string
  name: string
  key: string
  radio: string
  recorder: string
  flashlight: string
  badge: string
  posts: Post[]
  callSign: string
  shiftFrom: string
  shiftTo: string
  /** True when this empty slot was explicitly added via the Add Supervisor
   *  button (so the UI shows it as an assignable card). Default/unfilled
   *  supervisor slots are NOT shown until someone is assigned. */
  manualAdd?: boolean
}

export interface SecurityKey {
  number: number
  assignedTo: string
  status: 'good' | 'bad'
  comment: string
  oos?: boolean
}

export interface OtherKey {
  number: number
  description: string
  category: 'Mall Department' | 'Contractors'
  available: boolean
  assignedTo: string
  oos?: boolean
}

export interface RadioSlot {
  id: string
  available: boolean
  assignedTo: string
  oos?: boolean
  manualOnDuty?: boolean
  /** FOM-compatible manual duty override: 'on' forces on-duty, 'off' forces off-duty, null/undefined = auto (shift-based) */
  manualDutyOverride?: 'on' | 'off' | null
}

export interface Flashlight {
  id: number
  available: boolean
  assignedTo: string
  permanent?: boolean
  oos?: boolean
}

export interface Badge {
  id: number
  status: 'inShadowBox' | 'hasPhysicalBadge' | 'onDuty'
}

export interface Recorder {
  id: string
  available: boolean
  assignedTo: string
  oos?: boolean
}

export interface SecondaryTask {
  task: string
  label: string
  detail: string
  time: string
  callSign: string
}

// Generic item — access badges & satellite phones share this shape
export interface GenericItem {
  id: string
  available: boolean
  assignedTo: string
  oos?: boolean
}

// ── Defaults (exact from FOM source) ──────────────────────────────────────

function makeEntry(role: string): PersonnelEntry {
  return { role, name: '', key: '', radio: '', recorder: '', flashlight: '', badge: '', posts: [] as Post[], callSign: '', shiftFrom: '', shiftTo: '' }
}

export const DEFAULT_PERSONNEL: PersonnelEntry[] = [
  makeEntry('Dir/AD/Lt'),
  makeEntry('Sergeant'),
  makeEntry('OIC'),
  makeEntry('Officer'),
  makeEntry('Officer'),
  makeEntry('Officer'),
  makeEntry('Officer'),
  makeEntry('Officer'),
  makeEntry('Officer'),
]

export const DEFAULT_KEYS: SecurityKey[] = Array.from({ length: 7 }, (_, i) => ({
  number: i + 1, assignedTo: '', status: 'good', comment: '', oos: false,
}))

export const DEFAULT_OTHER_KEYS: OtherKey[] = [
  // Mall Department
  { number: 8,  description: 'Spare Car Key',         category: 'Mall Department', available: true, assignedTo: '' },
  { number: 9,  description: 'Generator',              category: 'Mall Department', available: true, assignedTo: '' },
  { number: 10, description: 'Traffic Light Box',      category: 'Mall Department', available: true, assignedTo: '' },
  { number: 11, description: 'New Wing SMK detect',    category: 'Mall Department', available: true, assignedTo: '' },
  { number: 12, description: 'Sink Valve',             category: 'Mall Department', available: true, assignedTo: '' },
  { number: 13, description: 'Water Valve',            category: 'Mall Department', available: true, assignedTo: '' },
  { number: 14, description: 'Elevator Key',           category: 'Mall Department', available: true, assignedTo: '' },
  { number: 15, description: 'J Valve Key',            category: 'Mall Department', available: true, assignedTo: '' },
  { number: 17, description: 'H & M Key',              category: 'Mall Department', available: true, assignedTo: '' },
  { number: 19, description: 'Master Pad Lock',        category: 'Mall Department', available: true, assignedTo: '' },
  { number: 27, description: 'Electric Disc Tenant',   category: 'Mall Department', available: true, assignedTo: '' },
  { number: 28, description: 'J Parking Elevator',     category: 'Mall Department', available: true, assignedTo: '' },
  { number: 30, description: 'Emer Elevator Key',      category: 'Mall Department', available: true, assignedTo: '' },
  { number: 35, description: 'Emer Emply Bath Bypass', category: 'Mall Department', available: true, assignedTo: '' },
  // Contractors
  { number: 16, description: 'Castle Sprinkler',       category: 'Contractors', available: true, assignedTo: '' },
  { number: 18, description: 'Specified Electrical',   category: 'Contractors', available: true, assignedTo: '' },
  { number: 20, description: 'Reliable',               category: 'Contractors', available: true, assignedTo: '' },
  { number: 21, description: 'MACC',                   category: 'Contractors', available: true, assignedTo: '' },
  { number: 22, description: 'KONE',                   category: 'Contractors', available: true, assignedTo: '' },
  { number: 23, description: 'Ambius',                 category: 'Contractors', available: true, assignedTo: '' },
  { number: 24, description: 'Yes Energy (AMS)',       category: 'Contractors', available: true, assignedTo: '' },
  { number: 25, description: 'JS Networks',            category: 'Contractors', available: true, assignedTo: '' },
  { number: 26, description: 'Beales Plumbing',        category: 'Contractors', available: true, assignedTo: '' },
  { number: 29, description: 'JC Decaux',              category: 'Contractors', available: true, assignedTo: '' },
  { number: 31, description: 'Visual Merch Key',       category: 'Contractors', available: true, assignedTo: '' },
  { number: 32, description: 'Presto Pest Control',    category: 'Contractors', available: true, assignedTo: '' },
  { number: 33, description: 'I-Tech CCTV',            category: 'Contractors', available: true, assignedTo: '' },
  { number: 34, description: 'AArdvark',               category: 'Contractors', available: true, assignedTo: '' },
]

export const DEFAULT_FLASHLIGHTS: Flashlight[] = [
  { id: 1, available: false, assignedTo: 'Vehicle', permanent: true },
  { id: 2, available: true, assignedTo: '' },
  { id: 3, available: true, assignedTo: '' },
]

export const DEFAULT_SUP_RADIOS: RadioSlot[] = [
  { id: 'SGT-100', available: true, assignedTo: '' },
  { id: 'SGT-101', available: true, assignedTo: '' },
]

export const DEFAULT_OFF_RADIOS: RadioSlot[] = [
  '102','103','104','114','106','107','115','109','110','111','112','117',
].map(id => ({ id, available: true, assignedTo: '' }))

export const DEFAULT_OTHER_RADIOS: RadioSlot[] = [
  // Management — available:true = radio in cabinet (green), false = deployed with mgmt staff (blue)
  { id: '92',   available: true,  assignedTo: 'M-1'   },
  { id: '93',   available: true,  assignedTo: 'O-1'   },
  { id: '98',   available: true,  assignedTo: 'L-1'   },
  // Supervisor radios — always with sups, never toggled here (handled by SupRadioPanel)
  { id: '95',   available: false, assignedTo: 'S-01'  },
  { id: '97',   available: false, assignedTo: 'S-03'  },
  { id: '99',   available: false, assignedTo: 'S-02'  },
  // Engineering — available:true = in cabinet, false = deployed with eng staff
  { id: '105',  available: true,  assignedTo: 'E-2'   },
  { id: '113',  available: true,  assignedTo: 'E-3'   },
  { id: '47',   available: true,  assignedTo: 'E-4'   },
  { id: '50',   available: true,  assignedTo: 'E-5'   },
  { id: '116',  available: true,  assignedTo: 'E-6'   },
  // FCPD — available:false so dashboard isOnDuty() check governs display
  { id: '108',  available: false, assignedTo: 'SC-82' },
  { id: '118',  available: false, assignedTo: 'SC-81' },
  // Available pool
  { id: '10',   available: true,  assignedTo: '' },
  { id: '28',   available: true,  assignedTo: '' },
  { id: '51',   available: true,  assignedTo: '' },
  { id: '52',   available: true,  assignedTo: '' },
  { id: 'R-23', available: true,  assignedTo: '' },
]

export const DEFAULT_RECORDERS: Recorder[] = [
  { id: '1',  available: true,  assignedTo: '' },
  { id: '2',  available: true,  assignedTo: '' },
  { id: '3',  available: true,  assignedTo: '' },
  { id: '4',  available: true,  assignedTo: '' },
  { id: '5',  available: true,  assignedTo: '' },
  { id: '6',  available: true,  assignedTo: '' },
  { id: '7',  available: true,  assignedTo: '' },
  { id: '8',  available: true,  assignedTo: '' },
  { id: '9',  available: true,  assignedTo: '' },
  { id: '10', available: false, assignedTo: 'OOS', oos: true },
  { id: '11', available: true,  assignedTo: '' },
]

export const DEFAULT_BADGES: Badge[] = Array.from({ length: 16 }, (_, i) => ({
  id: i + 1, status: 'inShadowBox',
}))

export const DEFAULT_ACCESS_BADGES: GenericItem[] = Array.from({ length: 10 }, (_, i) => ({
  id: String(i + 1), available: true, assignedTo: '',
}))

export const DEFAULT_SAT_PHONES: GenericItem[] = [
  { id: '1', available: true, assignedTo: '' },
]

// ── Helpers ────────────────────────────────────────────────────────────────

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : fallback
  } catch { return fallback }
}

/**
 * Like load() but treats an empty array as "no data" and returns the fallback.
 *
 * Used for equipment inventories (security keys, flashlights, radios, recorders)
 * where an empty list means "uninitialized" rather than "user cleared everything."
 *
 * This is needed because the D1 sync layer can write '[]' to localStorage when
 * a fresh shift_data row is pulled (the column DEFAULT is '[]'), which would
 * otherwise wipe out the FOM-default inventory built into this file.
 *
 * IMPORTANT: when the fallback is used, we ALSO write it back to localStorage so
 * cross-tab/cross-origin consumers (e.g. dashboard.html which reads fom_* keys
 * directly) see the same defaults that React state has. Without this writeback,
 * the dashboard would render "None" for any inventory that nobody has touched
 * yet, even though the React app shows the defaults populated.
 */
function loadInventory<T extends unknown[]>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      try { localStorage.setItem(key, JSON.stringify(fallback)) } catch { /* silent */ }
      return fallback
    }
    const parsed = JSON.parse(raw) as T
    if (!Array.isArray(parsed) || parsed.length === 0) {
      try { localStorage.setItem(key, JSON.stringify(fallback)) } catch { /* silent */ }
      return fallback
    }
    return parsed
  } catch { return fallback }
}

function save(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* silent */ }
  markDirty()
  markKeyDirty(key)  // last-write-wins: track per-key local write time
}

// ── Personnel normalizer ───────────────────────────────────────────────────
// FOM stores only assigned officers; Castellan always needs exactly 9 slots.
// Supervisor roles: first 3 slots (Dir/AD/Lt, Sergeant, OIC)
// Officer slots: next 6

const SUP_ROLES = ['Dir/AD/Lt', 'Sergeant', 'OIC']
const OFFICER_ROLE = 'Officer'
const N_OFFICERS = 6

// All roles that qualify as supervisor slots
const SUP_SLOT_ROLES = ['Dir/AD/Lt', 'Sergeant', 'OIC', 'Lieutenant', 'Director of Security',
  'Assistant Director', 'Director', 'Developer']

function normalizePersonnel(raw: PersonnelEntry[]): PersonnelEntry[] {
  if (!Array.isArray(raw)) return DEFAULT_PERSONNEL

  // Normalize each entry — ensure posts is Post[] not string[]
  const entries: PersonnelEntry[] = raw.map(e => ({
    ...makeEntry(e.role || 'Officer'),
    ...e,
    posts: Array.isArray(e.posts)
      ? e.posts.map(p =>
          typeof p === 'string'
            ? { post: p, from: '', to: '', isBreak: p === 'Break' }
            : p
        )
      : [],
  }))

  // If we already have exactly 9 entries with the right structure, trust it
  // (FCPD entries in officer slots are allowed — their role is preserved as-is)
  if (entries.length === 9 && entries.slice(0,3).every(e => SUP_SLOT_ROLES.includes(e.role) || e.name === '')) {
    // Ensure officer slots (3-8) have correct role UNLESS they are FCPD
    return entries.map((e, i) => (
      i >= 3 && e.role !== 'FCPD' && !SUP_SLOT_ROLES.includes(e.role)
        ? { ...e, role: OFFICER_ROLE }
        : e
    ))
  }

  // Separate sup-class and officer-class entries (preserve all named entries)
  const supEntries = entries.filter(e => SUP_SLOT_ROLES.includes(e.role) || SUP_ROLES.includes(e.role))
  const officerEntries = entries.filter(e => !SUP_SLOT_ROLES.includes(e.role) && !SUP_ROLES.includes(e.role))

  // Build supervisor slots — at least one per default role, plus any extras
  const supSlots: PersonnelEntry[] = []
  const usedIndices = new Set<number>()

  for (const role of SUP_ROLES) {
    const idx = supEntries.findIndex((e, i) => !usedIndices.has(i) && e.role === role)
    if (idx >= 0) {
      supSlots.push(supEntries[idx])
      usedIndices.add(idx)
    } else {
      supSlots.push(makeEntry(role))
    }
  }

  // Add any remaining supervisor entries that weren't placed
  supEntries.forEach((e, i) => {
    if (!usedIndices.has(i) && e.name) supSlots.push(e)
  })

  // Build officer slots — fill from loaded officers, pad to N_OFFICERS
  // Preserve FCPD entries exactly — their role must not be overwritten
  const officerSlots: PersonnelEntry[] = officerEntries.map(e =>
    e.role === 'FCPD' ? e : { ...e, role: OFFICER_ROLE }
  )
  while (officerSlots.length < N_OFFICERS) {
    officerSlots.push(makeEntry(OFFICER_ROLE))
  }

  return [...supSlots, ...officerSlots]
}

function loadPersonnel(): PersonnelEntry[] {
  try {
    const raw = localStorage.getItem('fom_personnel')
    if (!raw) return DEFAULT_PERSONNEL
    return normalizePersonnel(JSON.parse(raw) as PersonnelEntry[])
  } catch { return DEFAULT_PERSONNEL }
}

// ── Store ──────────────────────────────────────────────────────────────────

interface DesktopState {
  personnel: PersonnelEntry[]
  deskOfficer: string
  supervisors: string[]
  securityKeys: SecurityKey[]
  flashlights: Flashlight[]
  supRadios: RadioSlot[]
  offRadios: RadioSlot[]
  otherRadios: RadioSlot[]
  recorders: Recorder[]
  badges: Badge[]
  accessBadges: GenericItem[]
  satPhones: GenericItem[]
  secondaryTasks: SecondaryTask[]
  notes: string[]
  currentDate: string
  shift: string
  weather: { temp: string; conditions: string }
  activeSection: string

  setPersonnel: (p: PersonnelEntry[]) => void
  updateOfficer: (index: number, patch: Partial<PersonnelEntry>) => void
  clearSlotByIdentity: (callSign: string, name: string) => void
  setDeskOfficer: (cs: string) => void
  setSupervisors: (names: string[]) => void
  otherKeys: OtherKey[]
  setOtherKeys: (keys: OtherKey[]) => void
  setSecurityKeys: (keys: SecurityKey[]) => void
  setFlashlights: (fl: Flashlight[]) => void
  setSupRadios: (r: RadioSlot[]) => void
  setOffRadios: (r: RadioSlot[]) => void
  setOtherRadios: (r: RadioSlot[]) => void
  setRecorders: (r: Recorder[]) => void
  setBadges: (b: Badge[]) => void
  setAccessBadges: (a: GenericItem[]) => void
  setSatPhones: (s: GenericItem[]) => void
  setSecondaryTasks: (t: SecondaryTask[]) => void
  setNotes: (n: string[]) => void
  oosIds: number[]
  supOosIds: number[]
  activatedBadgeIds: number[]
  setOosIds: (ids: number[]) => void
  setSupOosIds: (ids: number[]) => void
  setActivatedBadgeIds: (ids: number[]) => void
  setCurrentDate: (d: string) => void
  setShift: (s: string) => void
  setWeather: (w: { temp: string; conditions: string }) => void
  setActiveSection: (id: string) => void
  hydrateFromStorage: () => void
  resetShift: () => void
  addSupervisorSlot: (role: 'Dir/AD/Lt' | 'Sergeant' | 'OIC') => void
  toggleFcpd: (unit: 'SC-81' | 'SC-82') => void
  fcpdOnDuty: string[]
}

export const useDesktopStore = create<DesktopState>((set, get) => ({
  personnel:    loadPersonnel(),
  deskOfficer:  load('fom_deskOfficer', ''),
  supervisors:  load('fom_supervisors', [] as string[]),
  securityKeys: loadInventory('fom_securityKeys', DEFAULT_KEYS),
  otherKeys:    loadInventory('fom_otherKeys', DEFAULT_OTHER_KEYS),
  flashlights:  loadInventory('fom_flashlights', DEFAULT_FLASHLIGHTS),
  supRadios:    loadInventory('fom_supervisorRadios', DEFAULT_SUP_RADIOS),
  offRadios:    loadInventory('fom_officerRadios', DEFAULT_OFF_RADIOS),
  otherRadios:  loadInventory('fom_otherRadios', DEFAULT_OTHER_RADIOS),
  recorders:    loadInventory('fom_recorders', DEFAULT_RECORDERS),
  badges:       loadInventory('fom_badges', DEFAULT_BADGES),
  accessBadges: loadInventory('fom_accessBadges', DEFAULT_ACCESS_BADGES),
  satPhones:    loadInventory('fom_satPhones', DEFAULT_SAT_PHONES),
  secondaryTasks: load('fom_secondaryTasks', []),
  notes:        load('fom_notes', []),
  oosIds:       load('fom_oosIds', []),
  supOosIds:    load('fom_supOosIds', []),
  activatedBadgeIds: load('fom_activatedBadgeIds', []),
  currentDate:  load('fom_currentDate', new Date().toISOString().slice(0, 10)),
  shift:        load('fom_shift', (() => { const h = new Date().getHours(); return h >= 7 && h < 15 ? 'Morning' : h >= 15 && h < 23 ? 'Evening' : 'Night' })()),
  weather:      load('fom_weather', { temp: '', conditions: '' }),
  activeSection: (() => {
    try { return localStorage.getItem('fom_activeSection') || 'top' } catch { return 'top' }
  })(),
  fcpdOnDuty: (() => { try { return JSON.parse(localStorage.getItem('fom_fcpd_on_duty') || '[]') } catch { return [] } })() as string[],

  setPersonnel(p) { save('fom_personnel', p); set({ personnel: p }) },

  updateOfficer(index, patch) {
    const updated = get().personnel.map((o, i) => i === index ? { ...o, ...patch } : o)
    // If an officer slot was cleared (name removed), compact officer slots
    // so empty slots sink to the bottom of the officer grid (no mid-grid gaps)
    const isClearing = patch.name === '' || patch.callSign === ''
    if (isClearing) {
      const SUP_COUNT = 3
      const sups = updated.slice(0, SUP_COUNT)
      const offs = updated.slice(SUP_COUNT)
      // Named regular officers first, then FCPD, then empty slots
      const namedRegular = offs.filter(o => o.name && o.role !== 'FCPD')
      const namedFcpd    = offs.filter(o => o.name && o.role === 'FCPD')
      const empty        = offs.filter(o => !o.name)
      const compacted = [...sups, ...namedRegular, ...namedFcpd, ...empty]
      save('fom_personnel', compacted)
      set({ personnel: compacted })
      return
    }
    save('fom_personnel', updated)
    set({ personnel: updated })
  },

  // Clear a slot by the person's identity (callSign preferred, else name)
  // rather than by array index. The officer grid re-sorts on every personnel
  // change (regular → FCPD → empty), so a captured index can point at the
  // wrong card by the time the click handler runs — which made closing one
  // card clear two. Matching on identity removes exactly the intended person.
  clearSlotByIdentity(callSign, name) {
    const csUp = (callSign || '').toUpperCase()
    const nm = (name || '').trim()
    let matched = false
    const cleared = get().personnel.map(o => {
      if (matched) return o
      const hit = csUp
        ? (o.callSign || '').toUpperCase() === csUp
        : nm && (o.name || '').trim() === nm
      if (!hit) return o
      matched = true
      return { ...o, name: '', callSign: '', key: '', radio: '', recorder: '',
        flashlight: '', badge: '', posts: [], shiftFrom: '', shiftTo: '' }
    })
    if (!matched) return
    // Compact officer slots so the emptied card sinks to the bottom
    const sups = cleared.slice(0, 3)
    const offs = cleared.slice(3)
    const namedRegular = offs.filter(o => o.name && o.role !== 'FCPD')
    const namedFcpd    = offs.filter(o => o.name && o.role === 'FCPD')
    const empty        = offs.filter(o => !o.name)
    const compacted = [...sups, ...namedRegular, ...namedFcpd, ...empty]
    save('fom_personnel', compacted)
    set({ personnel: compacted })
  },

  setDeskOfficer(cs) { save('fom_deskOfficer', cs); set({ deskOfficer: cs }) },
  setSupervisors(names) { save('fom_supervisors', names); set({ supervisors: names }) },
  setSecurityKeys(keys) {
    save('fom_securityKeys', keys)
    set({ securityKeys: keys })
    mirrorOos('keys', keys.filter(k => k.oos).map(k => k.number))
  },
  setOtherKeys(keys) { save('fom_otherKeys', keys); set({ otherKeys: keys }) },
  setFlashlights(fl) {
    save('fom_flashlights', fl)
    set({ flashlights: fl })
    mirrorOos('flashlights', fl.filter(x => x.oos).map(x => x.id))
  },
  setSupRadios(r) {
    save('fom_supervisorRadios', r)
    set({ supRadios: r })
    mirrorOos('supervisorRadios', r.filter(x => x.oos).map(x => x.id))
  },
  setOffRadios(r) {
    save('fom_officerRadios', r)
    set({ offRadios: r })
    mirrorOos('officerRadios', r.filter(x => x.oos).map(x => x.id))
  },
  setOtherRadios(r) {
    save('fom_otherRadios', r)
    set({ otherRadios: r })
    mirrorOos('otherRadios', r.filter(x => x.oos).map(x => x.id))
  },
  setRecorders(r) {
    save('fom_recorders', r)
    set({ recorders: r })
    mirrorOos('recorders', r.filter(x => x.oos).map(x => x.id))
  },
  setBadges(b) { save('fom_badges', b); set({ badges: b }) },
  setAccessBadges(a) { save('fom_accessBadges', a); set({ accessBadges: a }) },
  setSatPhones(s) { save('fom_satPhones', s); set({ satPhones: s }) },
  setSecondaryTasks(t) { save('fom_secondaryTasks', t); set({ secondaryTasks: t }) },
  setNotes(n) {
    save('fom_notes', n)
    set({ notes: n })
    // Mirror notes into fom_daily_posting.notes so posting/mobile see desktop edits
    try {
      const raw = localStorage.getItem('fom_daily_posting')
      if (raw) {
        const posting = JSON.parse(raw) as { notes?: string; [k: string]: unknown }
        const joined = n.join('\n')
        if (posting.notes !== joined) {
          posting.notes = joined
          localStorage.setItem('fom_daily_posting', JSON.stringify(posting))
          try { window.dispatchEvent(new CustomEvent('castellan:posting-changed')) } catch { /* silent */ }
        }
      }
    } catch { /* silent */ }
  },
  setOosIds(ids) {
    save('fom_oosIds', ids)
    // Mirror into fom_oos_state.badges so the dashboard reads the same data
    try {
      const raw = localStorage.getItem('fom_oos_state')
      const state = raw ? JSON.parse(raw) : {}
      state.badges = ids
      localStorage.setItem('fom_oos_state', JSON.stringify(state))
      markKeyDirty('fom_oos_state') // LWW: protect edit (incl. removals) until pushed
      markDirty()
    } catch { /* silent */ }
    set({ oosIds: ids })
  },
  setSupOosIds(ids) {
    save('fom_supOosIds', ids)
    // Mirror into fom_oos_state.supBadges (separate from officer badges)
    try {
      const raw = localStorage.getItem('fom_oos_state')
      const state = raw ? JSON.parse(raw) : {}
      state.supBadges = ids
      localStorage.setItem('fom_oos_state', JSON.stringify(state))
      markKeyDirty('fom_oos_state') // LWW: protect edit (incl. removals) until pushed
      markDirty()
    } catch { /* silent */ }
    set({ supOosIds: ids })
  },
  setActivatedBadgeIds(ids) { save('fom_activatedBadgeIds', ids); set({ activatedBadgeIds: ids }) },
  setCurrentDate(d) { save('fom_currentDate', d); set({ currentDate: d }) },
  setShift(s) { save('fom_shift', s); set({ shift: s }) },
  setWeather(w) { save('fom_weather', w); set({ weather: w }) },
  setActiveSection(id) {
    try { localStorage.setItem('fom_activeSection', id) } catch { /* silent */ }
    set({ activeSection: id })
  },

  resetShift() {
    const today = new Date().toISOString().slice(0, 10)
    const h = new Date().getHours()
    const shift = h >= 7 && h < 15 ? 'Morning' : h >= 15 && h < 23 ? 'Evening' : 'Night'
    const keys = [
      'fom_personnel','fom_deskOfficer','fom_supervisors','fom_secondaryTasks',
      'fom_securityKeys','fom_officerRadios','fom_supervisorRadios','fom_otherRadios',
      'fom_recorders','fom_flashlights','fom_na_log',
      'fom_daily_posting','fom_security_summary','fom_notes',
      'fom_last_posting_merge',
      'fom_fcpd_on_duty', // FCPD units go off-duty at shift end like everyone else
      'fom_equipment_assignments', // clear stale test assignments
    ]
    keys.forEach(k => { try { localStorage.removeItem(k) } catch { /* silent */ } })
    set({
      personnel:    [...DEFAULT_PERSONNEL],
      deskOfficer:  '',
      supervisors:  [],
      fcpdOnDuty:   [], // clear FCPD toggle state so they don't self-heal back
      securityKeys: JSON.parse(JSON.stringify(DEFAULT_KEYS)),
      otherKeys:    JSON.parse(JSON.stringify(DEFAULT_OTHER_KEYS)),
      flashlights:  JSON.parse(JSON.stringify(DEFAULT_FLASHLIGHTS)),
      supRadios:    JSON.parse(JSON.stringify(DEFAULT_SUP_RADIOS)),
      offRadios:    JSON.parse(JSON.stringify(DEFAULT_OFF_RADIOS)),
      otherRadios:  JSON.parse(JSON.stringify(DEFAULT_OTHER_RADIOS)),
      recorders:    JSON.parse(JSON.stringify(DEFAULT_RECORDERS)),
      badges:       JSON.parse(JSON.stringify(DEFAULT_BADGES)),
      accessBadges: JSON.parse(JSON.stringify(DEFAULT_ACCESS_BADGES)),
      satPhones:    JSON.parse(JSON.stringify(DEFAULT_SAT_PHONES)),
      secondaryTasks: [],
      notes:        [],
      currentDate:  today,
      shift,
    })
    save('fom_currentDate', today)
    save('fom_shift', shift)
    // OOS sticky: re-apply OOS flags after reset (they survive resets)
    try {
      const oosRaw = localStorage.getItem('fom_oos_state')
      if (oosRaw) {
        const oos = JSON.parse(oosRaw)
        const s = useDesktopStore.getState()
        const applyOos = <T extends { id?: any; number?: any; oos?: boolean; available?: boolean; assignedTo?: string }>(arr: T[], ids: string[], idKey: 'id' | 'number'): T[] =>
          arr.map(item => {
            const key = String(item[idKey])
            return ids.includes(key) ? { ...item, oos: true, available: false, assignedTo: '' } as T : item
          })
        if (oos.keys?.length)           set({ securityKeys: s.securityKeys.map(k => oos.keys.includes(k.number) ? { ...k, oos: true, assignedTo: '', status: 'good' } : k) })
        if (oos.officerRadios?.length)   set({ offRadios:    applyOos(s.offRadios,    oos.officerRadios,    'id') })
        if (oos.supervisorRadios?.length)set({ supRadios:    applyOos(s.supRadios,    oos.supervisorRadios, 'id') })
        if (oos.otherRadios?.length)     set({ otherRadios:  applyOos(s.otherRadios,  oos.otherRadios,      'id') })
        if (oos.otherRadiosDuty?.length) {
          const duty = oos.otherRadiosDuty as Array<{ id: string; o: 'on' | 'off' }>
          const current = useDesktopStore.getState().otherRadios
          set({ otherRadios: current.map(r => {
            const hit = duty.find(x => x.id === String(r.id))
            return hit ? { ...r, manualDutyOverride: hit.o } : r
          }) })
        }
        if (oos.flashlights?.length)     set({ flashlights:  applyOos(s.flashlights,  oos.flashlights,      'id') })
        if (oos.recorders?.length)       set({ recorders:    applyOos(s.recorders,    oos.recorders,        'id') })
        if (oos.badges?.length)          set({ oosIds: (oos.badges as (string|number)[]).map(Number).filter(n => !isNaN(n)) })
        if (oos.supBadges?.length)        set({ supOosIds: (oos.supBadges as (string|number)[]).map(Number).filter(n => !isNaN(n)) })
      }
    } catch { /* silent */ }
    // Push clear signal to cloud so ALL devices wipe on next pull
    setTimeout(() => {
      try {
        import('./sync').then(m => {
          import('../api/worker').then(({ workerPostV1 }) => {
            workerPostV1('/api/v1/shift/clear', {})
              .then(() => m.useSyncStore.getState().forcePull())
              .catch(() => {})
          })
        })
      } catch { /* silent */ }
    }, 300)
    // Stamp shift-key reset guard — blocks cloud restores for the ENTIRE current shift
    // (survives page refreshes, code updates, and app reinstalls)
    const resetNow = String(Date.now())
    sessionStorage.setItem('fom_shift_reset_ts', resetNow)
    localStorage.setItem('fom_shift_reset_ts_persist', resetNow)
    try {
      const etStr = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false })
      const [hStr, mStr] = etStr.split(':')
      const totalMins = parseInt(hStr) * 60 + parseInt(mStr)
      const shift = totalMins >= 6*60+55 && totalMins < 14*60+55 ? 'Morning'
                  : totalMins >= 14*60+55 && totalMins < 22*60+55 ? 'Evening' : 'Night'
      const dateStr = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })
      localStorage.setItem('fom_reset_shift_key', `${dateStr}-${shift}`)
    } catch { /* silent */ }
    // Stamp the shift key so the auto-reset watcher doesn't immediately re-fire.
    // MUST use getShiftKey() (ET-based) — NOT local getHours() — otherwise the keys
    // mismatch at the 5-min early boundary (e.g. 6:55 ET) and the reset fires on
    // every 60-second tick instead of just once.
    try {
      localStorage.setItem('fom_last_reset_shift_key', getShiftKey())
    } catch { /* silent */ }
  },

  toggleFcpd(unit) {
    const FCPD_UNITS: Record<string, { name: string; radio: string }> = {
      'SC-81': { name: 'M.P.O Jones, Ron',   radio: '118' },
      'SC-82': { name: 'Det. Kozicki, Alex', radio: '108' },
    }
    const info = FCPD_UNITS[unit]
    if (!info) return
    const current = get().fcpdOnDuty
    const isOn = current.includes(unit)
    const personnel = [...get().personnel]

    if (isOn) {
      // Remove from personnel — find the slot with this callSign
      const idx = personnel.findIndex(p => p.callSign === unit)
      if (idx >= 0) {
        personnel[idx] = { ...personnel[idx], name: '', callSign: '', radio: '', role: 'Officer', key: '', recorder: '', flashlight: '', badge: '', posts: [], shiftFrom: '', shiftTo: '' }
        // Compact: regular named first, FCPD named last, then empty
        const sups = personnel.slice(0, 3)
        const offs = personnel.slice(3)
        const namedRegular = offs.filter(o => o.name && o.role !== 'FCPD')
        const namedFcpd    = offs.filter(o => o.name && o.role === 'FCPD')
        const empty        = offs.filter(o => !o.name)
        const compacted = [...sups, ...namedRegular, ...namedFcpd, ...empty]
        save('fom_personnel', compacted)
        set({ personnel: compacted })
      }
      const newDuty = current.filter(cs => cs !== unit)
      localStorage.setItem('fom_fcpd_on_duty', JSON.stringify(newDuty))
      set({ fcpdOnDuty: newDuty })
    } else {
      // Add to LAST empty officer slot so regular officers always have priority on the left
      const emptyIdx = (() => {
        let last = -1
        for (let i = personnel.length - 1; i >= 3; i--) {
          if (!personnel[i].name) { last = i; break }
        }
        return last
      })()
      if (emptyIdx < 0) { alert('No empty officer slots available.'); return }
      personnel[emptyIdx] = {
        role: 'FCPD',
        name: info.name,
        callSign: unit,
        radio: info.radio,
        key: '', recorder: '', flashlight: '', badge: '',
        shiftFrom: '', shiftTo: '', posts: [],
      }
      save('fom_personnel', personnel)
      set({ personnel })
      const newDuty = [...current, unit]
      localStorage.setItem('fom_fcpd_on_duty', JSON.stringify(newDuty))
      set({ fcpdOnDuty: newDuty })
    }
    markDirty()
  },

  addSupervisorSlot(role) {
    markDirty()
    const current = get().personnel
    const newSlot: PersonnelEntry = {
      role, name: '', callSign: '', key: '', radio: '', recorder: '', badge: '', flashlight: '', shiftFrom: '', shiftTo: '', posts: [], manualAdd: true
    }
    // Supervisor region = all leading entries whose role is a supervisor role
    // (the first 3 defaults plus any extras previously added). We add a NEW
    // empty slot at the END of that region so:
    //   - if there's already an empty placeholder, the user can just use it;
    //   - if all current slots are filled, this creates a fresh empty card to
    //     assign into (which is what the Add Supervisor button is for).
    const SUP_REGION_ROLES = new Set(['Dir/AD/Lt', 'Sergeant', 'OIC', 'Lieutenant',
      'Director of Security', 'Assistant Director', 'Director', 'Developer'])
    // Find the index just past the last supervisor-region entry.
    let lastSupIdx = -1
    for (let i = 0; i < current.length; i++) {
      if (SUP_REGION_ROLES.has(current[i].role)) lastSupIdx = i
      else break // supervisors are always the leading block
    }
    const insertAt = lastSupIdx + 1
    const updated = [...current]
    updated.splice(insertAt, 0, newSlot)
    set({ personnel: updated })
    save('fom_personnel', updated)
  },

  hydrateFromStorage() {
    // First, try to merge any posting data → personnel before reading
    try { mergePostingToPersonnel() } catch { /* silent */ }

    set({
      personnel:    loadPersonnel(),
      deskOfficer:  load('fom_deskOfficer', ''),
      supervisors:  load('fom_supervisors', [] as string[]),
      securityKeys: loadInventory('fom_securityKeys', DEFAULT_KEYS),
      otherKeys:    loadInventory('fom_otherKeys', DEFAULT_OTHER_KEYS),
      flashlights:  loadInventory('fom_flashlights', DEFAULT_FLASHLIGHTS),
      supRadios:    loadInventory('fom_supervisorRadios', DEFAULT_SUP_RADIOS),
      offRadios:    loadInventory('fom_officerRadios', DEFAULT_OFF_RADIOS),
      otherRadios:  loadInventory('fom_otherRadios', DEFAULT_OTHER_RADIOS),
      recorders:    loadInventory('fom_recorders', DEFAULT_RECORDERS),
      badges:       loadInventory('fom_badges', DEFAULT_BADGES),
      accessBadges: loadInventory('fom_accessBadges', DEFAULT_ACCESS_BADGES),
      satPhones:    loadInventory('fom_satPhones', DEFAULT_SAT_PHONES),
      secondaryTasks: load('fom_secondaryTasks', []),
      notes:        load('fom_notes', []),
      oosIds:       load('fom_oosIds', []),
      supOosIds:    load('fom_supOosIds', []),
      activatedBadgeIds: load('fom_activatedBadgeIds', []),
      currentDate:  load('fom_currentDate', new Date().toISOString().slice(0, 10)),
      shift:        load('fom_shift', 'Morning'),
      weather:      load('fom_weather', { temp: '', conditions: '' }),
    })
    // OOS sticky: re-apply from fom_oos_state after hydration to prevent cloud overwrite
    try {
      const oosRaw = localStorage.getItem('fom_oos_state')
      if (oosRaw) {
        const oos = JSON.parse(oosRaw)
        const s = useDesktopStore.getState()
        if (oos.keys?.length)            set({ securityKeys: s.securityKeys.map((k: any) => oos.keys.includes(k.number) ? { ...k, oos: true, assignedTo: '', status: 'good' } : k) })
        if (oos.officerRadios?.length)    set({ offRadios:    s.offRadios.map((r: any) => oos.officerRadios.includes(String(r.id)) ? { ...r, oos: true, available: false, assignedTo: '' } : r) })
        if (oos.supervisorRadios?.length) set({ supRadios:    s.supRadios.map((r: any) => oos.supervisorRadios.includes(String(r.id)) ? { ...r, oos: true, available: false, assignedTo: '' } : r) })
        if (oos.otherRadios?.length)      set({ otherRadios:  s.otherRadios.map((r: any) => oos.otherRadios.includes(String(r.id)) ? { ...r, oos: true, available: false, assignedTo: '' } : r) })
        if (oos.otherRadiosDuty?.length) {
          const duty = oos.otherRadiosDuty as Array<{ id: string; o: 'on' | 'off' }>
          const current = useDesktopStore.getState().otherRadios
          set({ otherRadios: current.map((r: any) => {
            const hit = duty.find(x => x.id === String(r.id))
            return hit ? { ...r, manualDutyOverride: hit.o } : r
          }) })
        }
        if (oos.flashlights?.length)      set({ flashlights:  s.flashlights.map((r: any) => oos.flashlights.includes(String(r.id)) ? { ...r, oos: true, available: false, assignedTo: '' } : r) })
        if (oos.recorders?.length)        set({ recorders:    s.recorders.map((r: any) => oos.recorders.includes(String(r.id)) ? { ...r, oos: true, available: false, assignedTo: '' } : r) })
        if (oos.badges?.length)           set({ oosIds: (oos.badges as (string|number)[]).map(Number).filter(n => !isNaN(n)) })
        if (oos.supBadges?.length)         set({ supOosIds: (oos.supBadges as (string|number)[]).map(Number).filter(n => !isNaN(n)) })
      }
    } catch { /* silent */ }

    // ── FCPD self-heal ──────────────────────────────────────────────────
    // fcpdOnDuty (the toggle button state) lives in its own key that cloud
    // pulls never touch, but fom_personnel DOES get overwritten by pulls. So a
    // pull that lands without the FCPD entry makes the card vanish while the
    // button stays lit. Reconcile here: for every toggled-on FCPD unit that is
    // missing from the freshly-loaded personnel, re-insert it into an empty
    // officer slot. This runs after every pull/hydrate so the card always
    // matches the button.
    try {
      const FCPD_UNITS: Record<string, { name: string; radio: string }> = {
        'SC-81': { name: 'M.P.O Jones, Ron',   radio: '118' },
        'SC-82': { name: 'Det. Kozicki, Alex', radio: '108' },
      }
      const duty: string[] = JSON.parse(localStorage.getItem('fom_fcpd_on_duty') || '[]')
      if (duty.length) {
        const ppl = [...get().personnel]
        let changed = false
        for (const unit of duty) {
          const info = FCPD_UNITS[unit]
          if (!info) continue
          const present = ppl.some(p => (p.callSign || '').toUpperCase() === unit.toUpperCase())
          if (present) continue
          // Find last empty officer slot (index >= 3) so regulars stay on the left
          let slot = -1
          for (let i = ppl.length - 1; i >= 3; i--) { if (!ppl[i].name) { slot = i; break } }
          if (slot < 0) { ppl.push({ ...makeEntry('FCPD') }); slot = ppl.length - 1 }
          ppl[slot] = { role: 'FCPD', name: info.name, callSign: unit, radio: info.radio,
            key: '', recorder: '', flashlight: '', badge: '', shiftFrom: '', shiftTo: '', posts: [] }
          changed = true
        }
        if (changed) {
          save('fom_personnel', ppl)
          set({ personnel: ppl })
        }
      }
    } catch { /* silent */ }

    // Schedule notifications for the logged-in officer
    try {
      const sessionRaw = localStorage.getItem('fom_auth_session')
      if (sessionRaw) {
        const session = JSON.parse(sessionRaw) as { callSign?: string }
        const cs = (session.callSign || '').toUpperCase()
        if (cs) {
          const personnel = loadPersonnel()
          const me = personnel.find(p => (p.callSign || '').toUpperCase() === cs)
          if (me) {
            const breaks = (me.posts || []).filter((p: any) => p.isBreak || p.post === 'Break')
              .map((p: any) => ({ from: p.from || p.time, to: p.to || p.endTime }))
            scheduleOfficerNotifications(cs, me.shiftTo, breaks).catch(() => {})

            // ── Primary task (post) change detection ──────────────────────────
            // Fingerprint the officer's non-break posts. If it differs from the
            // last hydrate (and we HAD a previous fingerprint for this officer),
            // a supervisor changed their assignment — notify with the new post.
            try {
              const primaryPosts = (me.posts || []).filter((p: any) => !p.isBreak && p.post !== 'Break')
              const fpList = primaryPosts.map((p: any) => `${p.post}@${p.from || p.time || ''}-${p.to || p.endTime || ''}`)
              const fp = cs + '::' + fpList.join('|')
              const prevFp = localStorage.getItem('fom_my_posts_fp')
              if (prevFp && prevFp.startsWith(cs + '::') && prevFp !== fp && fpList.length > 0) {
                const prevList = prevFp.slice(cs.length + 2).split('|')
                const changed = fpList.find(x => !prevList.includes(x)) // new or modified post
                if (changed) {
                  const [postName, times] = changed.split('@')
                  notifyPostChange(postName, (times || '').replace('-', ' – ')).catch(() => {})
                }
              }
              localStorage.setItem('fom_my_posts_fp', fp)
            } catch { /* silent */ }
          }
        }
      }
    } catch { /* silent */ }
  },
}))

// Re-hydrate immediately when a sync pull completes — no component lifecycle dependency
if (typeof window !== 'undefined') {
  window.addEventListener('castellan:pull-complete', () => {
    useDesktopStore.getState().hydrateFromStorage()
  })
  // Also re-hydrate when posting form changes locally
  window.addEventListener('castellan:posting-changed', () => {
    // Clear merge hash so next merge runs
    try { localStorage.removeItem('fom_last_posting_merge') } catch { /* silent */ }
    useDesktopStore.getState().hydrateFromStorage()
  })

  // ── Auto shift reset ─────────────────────────────────────────────
  // Checks every minute whether the shift boundary has passed since the last
  // manual or auto reset. If it has, fires resetShift() automatically.
  // Shift boundaries (ET): Morning 07:00, Evening 15:00, Night 23:00.
  // ── Auto shift reset ──────────────────────────────────────────────────
  // Checks every minute whether the shift boundary has passed since the last
  // manual or auto reset. If it has, fires resetShift() automatically.
  // getShiftKey() is at module scope so resetShift() and checkShiftBoundary()
  // always produce identical keys — no mismatch at the 5-min early boundary.
  function checkShiftBoundary() {
    try {
      const currentKey  = getShiftKey()
      const lastResetKey = localStorage.getItem('fom_last_reset_shift_key') || ''
      if (lastResetKey && lastResetKey !== currentKey) {
        // Shift has changed since last reset — auto-clear
        console.info('[Castellan] Shift boundary crossed — auto-resetting:', lastResetKey, '→', currentKey)
        localStorage.setItem('fom_last_reset_shift_key', currentKey)
        // Clear the persistent reset guard so the new shift’s posting can merge
        localStorage.removeItem('fom_shift_reset_ts_persist')
        localStorage.removeItem('fom_reset_shift_key')
        useDesktopStore.getState().resetShift()
      } else if (!lastResetKey) {
        // First run — stamp current shift, don’t reset
        localStorage.setItem('fom_last_reset_shift_key', currentKey)
      }
    } catch { /* silent */ }
  }

  // Run immediately on boot, then every 60 seconds
  checkShiftBoundary()
  setInterval(checkShiftBoundary, 60_000)

  // ── Auto-remove officers when their shift ends ─────────────────────────
  // Checks every 2 minutes. When current ET time is >= an officer's shiftTo
  // (plus a 5-min grace period), their personnel slot is cleared on the desktop.
  // Auth token is NOT revoked so mobile notifications still work.
  // Supervisors (Dir/AD/Lt, Sergeant, OIC) are never auto-removed.
  const AUTO_REMOVE_EXEMPT = new Set(['Dir/AD/Lt', 'Sergeant', 'OIC', 'Developer',
    'Lieutenant', 'Director of Security', 'Assistant Director'])

  function checkShiftEndRemovals() {
    try {
      const etNow = new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
      })
      const [hStr, mStr] = etNow.split(':')
      const nowMins = parseInt(hStr) * 60 + parseInt(mStr)

      const { personnel } = useDesktopStore.getState()
      let changed = false
      const updated = personnel.map(p => {
        if (!p.name || !p.shiftTo || AUTO_REMOVE_EXEMPT.has(p.role)) return p
        const raw = p.shiftTo.replace(':', '').padStart(4, '0')
        const shiftToMins = parseInt(raw.slice(0, 2)) * 60 + parseInt(raw.slice(2, 4))
        if (isNaN(shiftToMins) || shiftToMins === 0) return p
        // Add 5-min grace period
        const removeMins = (shiftToMins + 5) % (24 * 60)
        // Handle midnight rollover
        const passed = removeMins > shiftToMins
          ? nowMins >= removeMins   // normal case (no midnight crossing)
          : nowMins >= removeMins || nowMins < shiftToMins  // midnight crossing
        if (!passed) return p
        changed = true
        import('./postingBridge').then(({ removeFromPosting }) => {
          if (p.callSign) removeFromPosting(p.callSign)
        }).catch(() => {})
        return { ...p, name: '', callSign: '', key: '', radio: '', recorder: '',
          flashlight: '', badge: '', posts: [], shiftFrom: '', shiftTo: '' }
      })
      if (changed) {
        try { localStorage.setItem('fom_personnel', JSON.stringify(updated)) } catch { /* silent */ }
        useDesktopStore.setState({ personnel: updated })
        markDirty()
      }
    } catch { /* silent */ }
  }

  setInterval(checkShiftEndRemovals, 2 * 60_000) // every 2 minutes
}

// ── Equipment auto-sync ────────────────────────────────────────────────────
// Mirrors FOM's useEffect hooks that watch personnel and update equipment
// assignedTo fields. Runs on every personnel change.
let _lastPersonnelHash = ''

useDesktopStore.subscribe((state) => {
  const personnelHash = state.personnel.map(p =>
    `${p.name}|${p.key}|${p.radio}|${p.recorder}|${p.flashlight}|${p.badge}|${p.role}|${(p.posts ?? []).map(x => (typeof x === 'object' ? x.post : x)).join(',')}`
  ).join(',')
  if (personnelHash === _lastPersonnelHash) return
  _lastPersonnelHash = personnelHash

  const personnel = state.personnel

  // Sync security keys
  const keys = state.securityKeys.map(k => {
    const holder = personnel.find(p => p.name && p.key === String(k.number))
    return { ...k, assignedTo: holder ? holder.name : (k.oos ? '' : '') }
  })
  if (JSON.stringify(keys) !== JSON.stringify(state.securityKeys)) {
    try { localStorage.setItem('fom_securityKeys', JSON.stringify(keys)) } catch {}
    useDesktopStore.setState({ securityKeys: keys })
  }

  // Sync officer radios
  const offRadios = state.offRadios.map(r => {
    if (r.oos) return r
    const holder = personnel.find(p => p.name && p.radio === String(r.id))
    return holder
      ? { ...r, available: false, assignedTo: holder.name }
      : (r.assignedTo && !personnel.some(p => p.name === r.assignedTo)
          ? { ...r, available: true, assignedTo: '' }
          : r)
  })
  if (JSON.stringify(offRadios) !== JSON.stringify(state.offRadios)) {
    try { localStorage.setItem('fom_officerRadios', JSON.stringify(offRadios)) } catch {}
    useDesktopStore.setState({ offRadios })
  }

  // Sync supervisor radios (SGT-100, SGT-101 from supRadios)
  const supRadios = state.supRadios.map(r => {
    if (r.oos) return r
    const holder = personnel.find(p => p.name && p.radio === String(r.id))
    return holder
      ? { ...r, available: false, assignedTo: holder.name }
      : (r.assignedTo && !personnel.some(p => p.name === r.assignedTo)
          ? { ...r, available: true, assignedTo: '' }
          : r)
  })
  if (JSON.stringify(supRadios) !== JSON.stringify(state.supRadios)) {
    try { localStorage.setItem('fom_supervisorRadios', JSON.stringify(supRadios)) } catch {}
    useDesktopStore.setState({ supRadios })
  }

  // Sync other radios — permanently assigned radios (mgmt/engineering/police/SGT)
  // are never touched. Only spare/available pool radios track personnel.
  // 95/97/99 are permanently assigned to S-01/S-03/S-02 and must never be
  // reassigned by the subscriber — the dashboard reads their fixed callSign.
  const PERMANENT_OTHER_IDS = ['92','93','98','95','97','99','105','113','47','50','116','108','118']
  const otherRadios = state.otherRadios.map(r => {
    if (r.oos) return r
    // Personnel-assigned radios (95/97/99): sync with personnel
    if (!PERMANENT_OTHER_IDS.includes(r.id)) {
      const holder = personnel.find(p => p.name && p.radio === String(r.id))
      if (holder) return { ...r, available: false, assignedTo: holder.name }
    }
    return r
  })
  if (JSON.stringify(otherRadios) !== JSON.stringify(state.otherRadios)) {
    try { localStorage.setItem('fom_otherRadios', JSON.stringify(otherRadios)) } catch {}
    useDesktopStore.setState({ otherRadios })
  }

  // Sync recorders
  const recorders = state.recorders.map(r => {
    if (r.oos) return r
    const holder = personnel.find(p => p.name && p.recorder === String(r.id))
    return holder
      ? { ...r, available: false, assignedTo: holder.name }
      : (r.assignedTo && !personnel.some(p => p.name === r.assignedTo)
          ? { ...r, available: true, assignedTo: '' }
          : r)
  })
  if (JSON.stringify(recorders) !== JSON.stringify(state.recorders)) {
    try { localStorage.setItem('fom_recorders', JSON.stringify(recorders)) } catch {}
    useDesktopStore.setState({ recorders })
  }

  // Sync flashlights (skip permanent ones — those are reserved like Vehicle)
  const flashlights = state.flashlights.map(f => {
    if (f.permanent) return f
    const holder = personnel.find(p => p.name && p.flashlight === String(f.id))
    return holder
      ? { ...f, available: false, assignedTo: holder.name }
      : (f.assignedTo && !personnel.some(p => p.name === f.assignedTo)
          ? { ...f, available: true, assignedTo: '' }
          : f)
  })
  if (JSON.stringify(flashlights) !== JSON.stringify(state.flashlights)) {
    try { localStorage.setItem('fom_flashlights', JSON.stringify(flashlights)) } catch {}
    useDesktopStore.setState({ flashlights })
  }

  // Auto-update desk officer — whoever has the Desk post wins.
  // Only update when the posting-derived Desk officer differs from what’s stored,
  // so manual selections from the dropdown are NOT silently overridden unless
  // the posting explicitly assigns someone to the Desk post.
  const deskOfficer = personnel.find(p =>
    p.name && p.posts?.some(post => typeof post === 'object' && post.post === 'Desk')
  )
  if (deskOfficer) {
    const currentDesk = load<string>('fom_deskOfficer', '')
    const newDesk = deskOfficer.name || deskOfficer.callSign
    // Always sync when posting has an explicit Desk assignment
    if (currentDesk !== newDesk) {
      save('fom_deskOfficer', newDesk)
      useDesktopStore.setState({ deskOfficer: newDesk })
    }
  }

  // Sync personnel → posting (one-way, non-destructive)
  // Only runs after equipment subscriber settles to avoid overwriting posting edits
  try { syncPersonnelToPosting() } catch { /* silent */ }

  // Keep FCPD officers always to the RIGHT of regular officers.
  // Re-sort officer slots (index 3+) after every personnel change:
  //   named regular → named FCPD → empty
  const sups2 = personnel.slice(0, 3)
  const offs2 = personnel.slice(3)
  const regNamed  = offs2.filter(o => o.name && o.role !== 'FCPD')
  const fcpdNamed = offs2.filter(o => o.name && o.role === 'FCPD')
  const empty2    = offs2.filter(o => !o.name)
  const sorted = [...sups2, ...regNamed, ...fcpdNamed, ...empty2]
  const sortedStr = JSON.stringify(sorted)
  if (sortedStr !== JSON.stringify(personnel)) {
    try { localStorage.setItem('fom_personnel', sortedStr) } catch {}
    useDesktopStore.setState({ personnel: sorted })
  }

  // Assign officer badges.
  // PRIMARY source: the officer's REAL badge number from the roster
  // (fom_roster mirrors D1 roster.badge_number). Badge numbers are assigned
  // per-officer in D1 and do NOT follow slot order or call sign — so we look
  // each officer up by call sign and use their stored badge. Only if an
  // officer has no roster badge do we fall back to the lowest free badge.
  // Supervisors are excluded — they don't carry officer badges.

  // Build a callSign -> badge_number map from the cached roster.
  const rosterBadgeByCs: Record<string, string> = {}
  try {
    const roster = JSON.parse(localStorage.getItem('fom_roster') ?? '{}') as Record<string, Array<{ callSign?: string; badgeNumber?: number | string | null }>>
    for (const cat in roster) {
      if (!Array.isArray(roster[cat])) continue
      for (const e of roster[cat]) {
        const cs = (e.callSign || '').toUpperCase()
        if (cs && e.badgeNumber != null && String(e.badgeNumber).trim() !== '') {
          rosterBadgeByCs[cs] = String(e.badgeNumber)
        }
      }
    }
  } catch { /* silent */ }

  // Each officer gets ONLY their real roster badge. If the roster has no badge
  // number for them, the badge stays BLANK — never a sequential/made-up number
  // (that was the original bug, e.g. S-15 showing 4 instead of his real 7).
  const updatedFromRoster = [...personnel]
  let rosterChanged = false
  personnel.forEach((p, idx) => {
    if (!(p.name && p.role === 'Officer')) return
    const realBadge = rosterBadgeByCs[(p.callSign || '').toUpperCase()] || ''
    // Set to the roster badge (or blank if none) — correct any wrong/stale value.
    if (p.badge !== realBadge) {
      updatedFromRoster[idx] = { ...updatedFromRoster[idx], badge: realBadge }
      rosterChanged = true
    }
  })

  if (rosterChanged) {
    try { localStorage.setItem('fom_personnel', JSON.stringify(updatedFromRoster)) } catch {}
    useDesktopStore.setState({ personnel: updatedFromRoster })
  }
})
